/* eslint-disable */

import styled, { CSSProp } from "styled-components"
import colors from "styles/colors"
import shadows from "styles/shadows"
import { ReactComponent as DownArrow } from "images/global/down-arrow.svg"
import { ReactComponent as ChevronBack } from "images/global/chevron-left.svg"
import { ReactComponent as PlayIcon } from "images/global/playIcon.svg"
import { ReactComponent as DownloadSVG } from "images/global/download.svg"
import text from "styles/text"
import media from "styles/media"
import { ButtonTypes } from "types"
import easing from "styles/easing"
import UniversalLink from "utils/Loader/UniversalLink"

type Props = ButtonTypes
type PropsWithColors = ButtonTypes & {
  colorStates: {
    background: {
      default: string
      hover: string
      active: string
      border: string | null
      shadow: CSSProp | null
    }
    text: {
      default: string
      hover: string
      active: string
    }
  }
}

const Button: React.FC<Props> = ({
  flip,
  arrow,
  back,
  play,
  banner = false,
  download,
  secondary,
  link,
  smallerButtonPadding,
  lightMode,
  passedRef,
  styledLink = false,
  className = "",
  children = null,
  onClick,
  rotate,
  submit,
  breakText = false,
  usePageColor,
  buttonColor,
  magenta,
  href,
}) => {
  let colorStates

  switch (true) {
    case lightMode && secondary:
      colorStates = {
        background: {
          default: "transparent",
          hover: colors.blue10,
          active: colors.blue50,
          border: colors.blue50,
          shadow: null,
        },
        text: {
          default: colors.blue400,
          hover: colors.blue400,
          active: colors.blue400,
        },
      }
      break
    case magenta:
      colorStates = {
        background: {
          default: colors.magenta400,
          hover: colors.white,
          active: colors.magenta10,
          border: null,
          shadow: shadows.lightModeButtonShadow,
        },
        text: {
          default: colors.white,
          hover: colors.magenta400,
          active: colors.magenta400,
        },
      }
      break
    case lightMode:
      colorStates = {
        background: {
          default: colors.blue400,
          hover: colors.white,
          active: colors.blue10,
          border: null,
          shadow: shadows.lightModeButtonShadow,
        },
        text: {
          default: colors.white,
          hover: colors.blue400,
          active: colors.blue400,
        },
      }
      break

    case secondary:
      colorStates = {
        background: {
          default: "transparent",
          hover: colors.blue600,
          active: colors.blue800,
          border: colors.seaFoam400,
          shadow: null,
        },
        text: {
          default: colors.seaFoam400,
          hover: colors.seaFoam400,
          active: colors.seaFoam400,
        },
      }
      break

    case banner:
      colorStates = {
        background: {
          default: "transparent",
          hover: colors.blue10,
          active: colors.blue50,
          border: colors.white,
          shadow: null,
        },
        text: {
          default: colors.white,
          hover: colors.blue400,
          active: colors.blue400,
        },
      }
      break

    default:
      colorStates = {
        background: {
          default: colors.white,
          hover: colors.blue400,
          active: colors.blue600,
          border: null,
          shadow: shadows.darkModeButtonShadow,
        },
        text: {
          default: colors.blue400,
          hover: colors.white,
          active: colors.white,
        },
      }
      break
  }

  return (
    <BtnEl
      ref={passedRef && passedRef}
      colorStates={colorStates}
      flip={flip}
      arrow={arrow}
      back={back}
      play={play}
      className={className}
      banner={banner}
      secondary={secondary}
      link={link}
      breakText={breakText}
      download={download}
      lightMode={lightMode}
      magenta={magenta}
      usePageColor={usePageColor}
      onClick={onClick}
      rotate={rotate}
      submit={submit}
      smallerButtonPadding={smallerButtonPadding}
      buttonColor={buttonColor}
      as={styledLink ? UniversalLink : "button"}
      to={styledLink ? href : undefined}
      onMouseDown={(e: React.MouseEvent) => {
        e.preventDefault()
      }}
      onMouseUp={(e: React.MouseEvent) => {
        e.preventDefault()
      }}
    >
      {back ? <ChevronBack /> : null}

      {play ? <PlayIcon /> : null}
      {children}
      {download && <DownloadSVG />}

      {arrow ? <DownArrow /> : null}
    </BtnEl>
  )
}

const BtnEl = styled.button<PropsWithColors>`
  ${props => props.colorStates.background.shadow}
  white-space: ${({ breakText }) => (breakText ? "revert" : "pre")};
  transition-timing-function: ${easing.button};
  transition: 300ms;
  transition-property: color, background-color;
  -moz-user-select: none;
  -khtml-user-select: none;
  user-select: none;
  ${props =>
    props.smallerButtonPadding
      ? text.bodyS
      : props.secondary
      ? text.buttonR
      : text.buttonL}
  z-index: 0;

  border: ${({ colorStates }) =>
    colorStates.background.border
      ? `1px solid ${colorStates.background.border}`
      : null};
  ${media.fullWidth} {
    border-radius: 40px;
    width: fit-content;
    max-width: ${({ breakText }) => (breakText ? "135px" : "fit-content")};
    height: ${({ smallerButtonPadding }) =>
      smallerButtonPadding ? "30px" : "55px"};
    ${({ breakText }) => breakText && "height: fit-content"};

    padding: ${({ smallerButtonPadding }) =>
      smallerButtonPadding ? "5px 15px" : "16px 30px"};
    ${({ back, play, arrow, download }) =>
      back || play // props that have an icon on left side
        ? "padding-left: 25px; padding-right: 30px;"
        : arrow || download
        ? "padding-left: 30px; padding-right: 25px;"
        : null}
  }
  ${media.tablet} {
    border-radius: 5.371vw;
    ${({ submit }) => (submit ? `${text.bodyS}` : null)};
    width: ${({ submit }) => (submit ? "7.422vw" : "fit-content")};
    height: ${({ submit }) => (submit ? "2.93vw" : "5.371vw")};
    border: ${({ secondary, lightMode, banner }) =>
      banner
        ? `1px solid ${colors.white}`
        : secondary
        ? lightMode
          ? `0.098vw solid ${colors.blue50}`
          : `0.098vw solid ${colors.blue600}`
        : "none"};
    padding: 1.563vw 2.93vw;
    ${({ back, play, arrow, download }) =>
      back || play // props that have an icon on left side
        ? "padding-left: 2.441vw; padding-right: 2.93vw;"
        : arrow || download
        ? "padding-left: 2.93vw; padding-right: 2.441vw;"
        : null}
  }

  ${media.mobile} {
    border-radius: ${({ banner }) => (banner ? "10.667vw" : "14.667vw")};
    ${props => props.banner && text.buttonS};
    width: ${({ banner }) => (banner ? "fit-content" : "100%")};
    height: ${({ banner, smallerButtonPadding }) =>
      banner ? "7.2vw" : smallerButtonPadding ? "8vw" : "14.667vw"};
    border: ${({ secondary, lightMode, banner }) =>
      banner
        ? `1px solid ${colors.white}`
        : secondary
        ? lightMode
          ? `0.267vw solid ${colors.blue50}`
          : `0.267vw solid ${colors.blue600}`
        : "none"};
    padding: ${({ smallerButtonPadding, banner }) =>
      banner ? "1.067vw 4vw" : smallerButtonPadding ? "0 4vw" : "0 8vw"};
    ${({ back, play, arrow, download }) =>
      back || play // props that have an icon on left side
        ? "padding-left: 6.667vw; padding-right: 8vw;"
        : arrow || download
        ? "padding-left: 8vw; padding-right: 6.667vw;"
        : null}
  }

  path {
    transition-timing-function: ${easing.button};
    transition: 300ms;
    transition-property: fill, stroke;
    ${({ arrow, lightMode, secondary }) => {
      if (arrow) {
        switch (true) {
          case lightMode && secondary:
            return `fill : ${colors.blue400};`
          case secondary:
            return `fill : ${colors.seaFoam400};`
          case lightMode:
            return `fill : ${colors.white};`
          default:
            return `fill : ${colors.blue400};`
        }
      } else {
        switch (true) {
          case lightMode && secondary:
            return `stroke : ${colors.blue400};`
          case secondary:
            return `stroke : ${colors.seaFoam400};`
          case lightMode:
            return `stroke : ${colors.white};`
          default:
            return `stroke : ${colors.blue400};`
        }
      }
    }}
  }
  padding: ${({ smallerButtonPadding }) =>
    smallerButtonPadding ? "0.241vw 0.904vw" : "0.964vw 1.807vw"};
  ${({ back, play, arrow, download }) =>
    back || play // props that have an icon on left side
      ? "padding-left: 1.506vw; padding-right: 1.807vw;"
      : arrow || download
      ? "padding-left: 1.807vw; padding-right: 1.506vw;"
      : null}
  background-color: ${({
    lightMode,
    buttonColor,
    usePageColor,
    colorStates,
  }) => {
    switch (true) {
      case usePageColor && lightMode:
        return buttonColor
      default:
        return colorStates.background.default
    }
  }};
  border-radius: 3.313vw;
  width: fit-content;
  height: 3.313vw;

  cursor: pointer;
  display: flex;
  flex-direction: ${({ flip }) => (flip ? "row-reverse" : "row")};
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  color: ${({ colorStates }) => colorStates.text.default};

  svg {
    ${media.fullWidth} {
      box-shadow: none;
      width: ${props => (props.play ? "35px" : "25px")};
      height: ${props => (props.play ? "35px" : "25px")};
      margin: 0;
      padding: 0;
      ${({ back, play }) =>
        back || play
          ? "margin-left: 0 ; margin-right: 10px;"
          : "margin-left: 10px ; margin-right: 0;"}
      ${({ arrow, rotate }) =>
        arrow
          ? `transform: rotate(-${
              rotate ? rotate + 90 : 90
            }deg); margin-right:0; margin-left: 10px;`
          : null}
    }
    ${media.tablet} {
      box-shadow: none;
      width: ${props => (props.play ? "3.418vw" : "2.441vw")};
      height: ${props => (props.play ? "3.418vw" : "2.441vw")};
      margin: 0;
      padding: 0;
      ${({ back, play }) =>
        back || play
          ? "margin-left: 0 ; margin-right: 0.977vw;"
          : "margin-left: 0.977vw ; margin-right: 0;"}
      ${({ arrow, rotate }) =>
        arrow
          ? `transform: rotate(-${
              rotate ? rotate + 90 : 90
            }deg); margin-right:0; margin-left: 0.977vw;`
          : null};
    }
    ${media.mobile} {
      box-shadow: none;
      width: ${props => (props.play ? "9.333vw" : "6.667vw")};
      height: ${props => (props.play ? "9.333vw" : "6.667vw")};
      margin: 0;
      padding: 0;
      ${({ back, play }) =>
        back || play
          ? "margin-left: 0 ; margin-right: 2.667vw;"
          : "margin-left: 2.667vw ; margin-right: 0;"}
      ${({ arrow, rotate }) =>
        arrow
          ? `transform: rotate(-${
              rotate ? rotate + 90 : 90
            }deg); margin-right:0; margin-left: 2.667vw;`
          : null};
    }
    box-shadow: none;
    width: ${props => (props.play ? "2.108vw" : "1.506vw")};
    height: ${props => (props.play ? "2.108vw" : "1.506vw")};
    margin: 0;
    padding: 0;
    ${({ back, play }) =>
      back || play
        ? "margin-left: 0 ; margin-right: 0.602vw;"
        : "margin-left: 0.602vw ; margin-right: 0;"}
    ${({ arrow, rotate }) =>
      arrow
        ? `transform: rotate(-${
            rotate ? rotate + 90 : 90
          }deg); margin-right:0; margin-left: 0.602vw;`
        : null}
  }
  ${media.hover} {
    &:hover {
      color: ${({ lightMode, secondary, colorStates, play }) => {
        switch (true) {
          case lightMode && secondary:
            return colorStates.text.hover
          case play:
            return colors.blue400
          case lightMode:
            return colors.blue400
          default:
            return colorStates.text.hover
        }
      }};
      background-color: ${({ lightMode, secondary, colorStates, play }) => {
        switch (true) {
          case lightMode && secondary:
            return colorStates.background.hover
          case play:
            return colors.lightMode
          case lightMode:
            return colors.white
          default:
            return colorStates.background.hover
        }
      }};
      path {
        ${({ arrow, play, lightMode, secondary }) => {
          if (play) {
            return null
          }
          if (arrow) {
            // fill
            switch (true) {
              case lightMode && secondary:
                return `fill : ${colors.blue400};`
              case secondary:
                return `fill : ${colors.seaFoam400};`
              case lightMode:
                return `fill : ${colors.blue400};`
              default:
                return `fill : ${colors.white};`
            }
          } else {
            // stroke
            switch (true) {
              case lightMode && secondary:
                return `stroke : ${colors.blue400};`
              case secondary:
                return `stroke : ${colors.seaFoam400};`
              case lightMode:
                return `stroke : ${colors.blue400};`
              default:
                return `stroke : ${colors.white};`
            }
          }
        }}
      }
    }
  }
  :active {
    color: ${({ lightMode, play, colorStates }) => {
      switch (true) {
        case play:
          return colors.blue400

        case lightMode:
          return colors.blue400

        default:
          return colorStates.text.active
      }
    }};
    background-color: ${({ lightMode, secondary, play, colorStates }) => {
      switch (true) {
        case play:
          return colors.blue10
        case lightMode && secondary:
          return colors.blue50
        case lightMode:
          return colors.blue10
        default:
          return colorStates.background.active
      }
    }};
    path {
      ${({ arrow, play, lightMode, secondary }) => {
        if (play) {
          return null
        }
        if (arrow) {
          // fill
          switch (true) {
            case lightMode && secondary:
              return `fill : ${colors.blue400};`

            case secondary:
              return `fill : ${colors.seaFoam400};`

            case lightMode:
              return `fill : ${colors.blue400};`

            default:
              return `fill : ${colors.white};`
          }
        } else {
          // fill
          switch (true) {
            case lightMode && secondary:
              return `stroke : ${colors.blue400};`

            case secondary:
              return `stroke : ${colors.seaFoam400};`

            case lightMode:
              return `stroke : ${colors.blue400};`

            default:
              return `stroke : ${colors.white};`
          }
        }
      }}
    }
  }
`

export default Button
